<script setup>
import { DEV_MODE } from '@/config/dev-mode';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import axios from 'axios';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { getTicketCountAPI, getAnalysisLogAPI } from '@/api/AnalysisApi';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { useMonthlyFortuneFreeData } from '@/composables/saju-insight/useMonthlyFortuneFreeData';
import {
  MONTHLY_FORTUNE_CARD_TYPE_ID,
  MONTHLY_FORTUNE_PARENT_TYPE_ID,
} from '@/components/saju-insight/monthly-fortune/tocCards';
import { formatConsoleMessage, isObjectEmpty } from '@/composables/functions';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import overviewSajuSummary01 from '@/pages/mobile/analysis-saju-monthly-fortune/_summary_01.vue';
import overviewSajuSummary02 from '@/pages/mobile/analysis-saju-monthly-fortune/_summary_02.vue';
import MonthlyFortuneMobileLetter from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileLetter.vue';
import MonthlyFortuneMobileEnergy from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileEnergy.vue';
import MonthlyFortuneMobileLucky from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileLucky.vue';
import MonthlyFortuneMobileRomance from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileRomance.vue';
import MonthlyFortuneMobileHealth from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileHealth.vue';
import MonthlyFortuneMobileRomanceStyle from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileRomanceStyle.vue';
import MonthlyFortuneMobileMoney from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileMoney.vue';
import MonthlyFortuneMobileCapability from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileCapability.vue';
import MonthlyFortuneMobileCaution from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileCaution.vue';
import MonthlyFortuneMobileMode from '@/components/saju-insight/monthly-fortune/mobile/MonthlyFortuneMobileMode.vue';
import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import { useSajuData } from '@/composables/saju-insight/useSajuData';

definePage({ meta: { layout: 'empty' } });

const emit = defineEmits(['close']);

const router = useRouter();
const accessToken = ref(null);
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();

const { sajuData } = storeToRefs(analysisDataStore);

// =============== PC 테스트 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

if (userId.value) {
  // PC 버전에서 userId가 있으면 로그인 상태로 판단
  accessToken.value = true;
}

// =============== 웹뷰 통신 ===============
/** 모바일 사용자 정보 */
// const accessToken = ref(null);
const authUserId = ref(null);
const guestUserId = ref(null);
const isInitializing = ref(true);

/** 앱으로 메시지 전송 */
const sendMessageToApp = ({ action, message, route }) => {
  if (window.ReactNativeWebView) {
    const messageData = {
      action,
      payload: {
        message,
        route,
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
  }
};

const debuggingHandler = ({ action, message }) => {
  if (DEV_MODE) {
    switch (action) {
      case 'debug':
        console.log(formatConsoleMessage(message));
        break;
      case 'error':
        console.error(formatConsoleMessage(message));
        break;
      default:
        console.warn(`Unknown debug action: ${action}`);
        break;
    }
  }

  sendMessageToApp({ action, message });
};

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  isInitializing.value = true;
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // if (data.action === 'refresh') {
    //   sendMessageToApp({ action: 'close' })
    // }

    // message 수신되었을 때
    if (data.action === 'init') {
      accessToken.value = data?.payload?.accessToken;
      authUserId.value = data?.payload?.authUserId;
      mobileSajuLnbStore.setGuestUserId(data?.payload?.guestUserId);
      mobileSajuLnbStore.setUserName(data?.payload?.userName);
      mobileSajuLnbStore.setBottomInset(data?.payload?.bottom);

      axios.defaults.headers.common['Authorization'] = accessToken?.value;
      axios.defaults.headers.common['authUserId'] = authUserId?.value;
    }
    if (data.action === 'reset') {
      resetPopup();
    }
    if (data.action === 'refresh') {
      if (guestUserId.value && !isObjectEmpty(sajuData.value)) {
        refreshMonthlyFortunePageData();
      }
    }
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
  } finally {
    isInitializing.value = false;
  }
};

// =============== 사주 데이터 ===============
const { getSajuParams, formatSajuParams, getSajuData } = useSajuData();

const isLoadingSajuData = ref(false);
/** 사주 데이터 초기화 및 파생값 계산 */
const initializeSajuData = async () => {
  isLoadingSajuData.value = true;
  try {
    const sajuParams = await getSajuParams(guestUserId.value);
    if (!sajuParams) return;

    const formattedSajuParams = formatSajuParams(sajuParams, analysisDataStore.paramData);
    const sajuResult = await getSajuData(formattedSajuParams);
    if (!sajuResult) return;

    analysisStore.setUserName(sajuResult?.inputdata?.name);
    analysisDataStore.setSajuData(sajuResult);
  } catch (e) {
    debuggingHandler({ action: 'error', message: `상세분석 > 사주정보 요청 실패: ${e}` });
  } finally {
    isLoadingSajuData.value = false;
  }
};

// =============== 1. 이용중인 낭만월운 정보 ===============

/** 이용중인 낭만월운 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 낭만월운 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 낭만월운 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('monthlyFortune');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `낭만월운 > 1. 이용중인 낭만월운 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 2. 낭만월운 데이터 ===============
/** 낭만월운 데이터 요청 중 상태 */
const isFetchingMonthlyFortune = ref(false);

/** 낭만월운 데이터 요청 완료 여부 */
const hasFetchedMonthlyFortune = ref(false);

/** 낭만월운 데이터 요청 params */
const monthlyFortuneParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestUserId.value,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 5,
  };
});

/** 낭만월운 기본 데이터 요청 */
const fetchSubData = async () => {
  hasFetchedMonthlyFortune.value = false;
  isFetchingMonthlyFortune.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.monthlyFortuneSubData)) {
      const subDataParams = {
        ...monthlyFortuneParams.value,
        analysisTypeId: 8,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      // TODO: 데이터 구조에 맞게 수정 필요
      // if (result?.data?.월운종합분석) {
      //   analysisDataStore.setMonthlyFortuneSubData(result?.data?.월운종합분석);
      //   analysisDataStore.setMonthlyFortuneData({});

      //   hasFetchedMonthlyFortune.value = true;
      // }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `낭만월운 > 2-1. 기본 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingMonthlyFortune.value = false;
  }
};

/** 내 낭만월운 데이터 요청 */
const fetchMonthlyFortuneData = async () => {
  hasFetchedMonthlyFortune.value = false;
  isFetchingMonthlyFortune.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.monthlyFortuneData)) {
      const result = await fetchBaseServiceAPI(monthlyFortuneParams.value);

      if (result?.data) {
        // TODO: 데이터 구조에 맞게 수정 필요
        // analysisDataStore.setMonthlyFortuneData(result?.data);
        // analysisDataStore.setMonthlyFortuneSubData({});

        hasFetchedMonthlyFortune.value = true;

        analysisStore.setAnalysisResult(result?.data);
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `낭만월운 > 2-2. 분석권 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingMonthlyFortune.value = false;
  }
};

/** 낭만월운 데이터 초기화 */
const initMonthlyFortuneData = async () => {
  if (!serviceData.monthlyFortune.permission.value) {
    await fetchSubData();
  } else {
    await fetchMonthlyFortuneData();
  }
};

/** 낭만월운 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 낭만월운 페이지 데이터 새로고침 */
const refreshMonthlyFortunePageData = async () => {
  isRefreshingPageData.value = true;
  try {
    await Promise.all([
      getAnalysisInfo(),
      analysisStore.getAnalysisLog(guestUserId.value, 10), // 낭만월운 typeId: 10
      // 자식 typeId(42~46) 권한 판정을 위해 구매이력 전체 캐시
      analysisStore.fetchMonthlyFortunePurchaseHistory?.(guestUserId.value),
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
    ]);
    await initMonthlyFortuneData();
    // 권한 캐시 확정 후 낭만월운 본문 데이터 조회 (구매여부에 따라 suggestionFlag 자동 전환)
    fetchMonthlyFortunePaidNow();
    // 드로어에서 보여줄 ±0/+1/+2 의 다른 달도 백그라운드 prefetch
    prefetchMonthlyFortuneNeighborMonths();
  } catch (e) {
    debuggingHandler({ action: 'error', message: `낭만월운 > 페이지 새로고침 실패: ${e}` });
  } finally {
    isRefreshingPageData.value = false;
  }
};

// =============== 3. 컨텐츠 평가 ===============
/** 컨텐츠 평가 여부 */
const ratingStatus = ref(false);

/** 컨텐츠 평가 점수 */
const ratingResult = ref(0);

/** 컨텐츠 평가 조회하기 */
const getSajuMonthlyFortuneRate = async () => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      serviceName: 'MONTHLY_FORTUNE',
      objectKey,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      ratingResult.value = result.score;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      debuggingHandler({
        action: 'error',
        message: `낭만월운 > 3-1. 평가 조회 실패 status: ${e?.response?.status}`,
      });
      ratingStatus.value = false;
      ratingResult.value = 0;
    }
  }
};

/** 컨텐츠 평가하기 */
const postSajuMonthlyFortuneRate = async (score) => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      score,
      serviceName: 'MONTHLY_FORTUNE',
      objectKey,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getSajuMonthlyFortuneRate();
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `낭만월운 > 3-2. 평가하기 실패: ${e}` });
  }
};

const showPopAnalysisInfoDo = async () => {
  if (isRefreshingPageData.value) return;

  // 낭만월운권 사용중일 때,
  if (serviceData.monthlyFortune.permission.value) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < serviceContents.monthlyFortune.value?.pointPrice &&
    serviceData.monthlyFortune.ticketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    if (!isObjectEmpty(sajuData.value)) {
      await refreshMonthlyFortunePageData();
      await getSajuMonthlyFortuneRate();
    } else {
      await initializeSajuData();
      await refreshMonthlyFortunePageData();
      await getSajuMonthlyFortuneRate();
    }
  }
});

// =============== 하단 탭 영역 (직업추천 & 용어해석) ===============
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  if (!serviceData.monthlyFortune.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

const showSummary02 = ref(false); // overviewSajuSummary02 표시 여부

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

/** 팝업 닫기 — 카드 콘텐츠 진입 상태면 목차로 복귀, 아니면 상위 페이지로 */
const close = () => {
  if (selectedCard.value) {
    selectedCard.value = null;
    return;
  }
  if (window.ReactNativeWebView) {
    router.push('/mobile/saju-lnb');
  } else {
    router.back();
  }
};

const resetPopup = () => {
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({
      action: 'route',
      route: {
        screen: 'points',
        shortageAmount,
      },
    });
  }
};

const handelShowSummary02Open = () => {
  showSummary02.value = true;
};

const handleGoPurchaseHistory = () => {
  // TODO: 구매한 내역 페이지로 이동 (앱 라우팅 스펙 확정 후)
  debuggingHandler({ action: 'debug', message: '낭만월운 > 구매한 내역 클릭' });
};

// =============== 낭만월운 본문 데이터 (paidData) ===============
const paidYear = computed(() => analysisStore.monthlyFortuneSelectedYear);
const paidMonth = computed(() => analysisStore.monthlyFortuneSelectedMonth);

const resolvePurchasedMonthlyFortuneTypeIds = (year, month) => {
  if (!guestUserId.value) return [];
  const tids = [
    MONTHLY_FORTUNE_PARENT_TYPE_ID,
    ...Object.values(MONTHLY_FORTUNE_CARD_TYPE_ID),
  ];
  return tids.filter((tid) =>
    analysisStore.hasMonthlyFortuneAccess(guestUserId.value, year, month, tid),
  );
};

const purchasedMonthlyFortuneTypeIds = computed(() =>
  resolvePurchasedMonthlyFortuneTypeIds(paidYear.value, paidMonth.value),
);

const {
  freeData: monthlyFortunePaidData,
  ensureFreeData: ensureMonthlyFortuneData,
  fetchFor: fetchMonthlyFortuneFor,
  invalidateMonth: invalidateMonthlyFortuneMonth,
} = useMonthlyFortuneFreeData(
  guestUserId,
  paidYear,
  paidMonth,
  resolvePurchasedMonthlyFortuneTypeIds,
);

/** 현재월 ±0/+1/+2 본문 데이터를 백그라운드 프리페치 (드로어에서 월 전환 시 로딩 제거) */
const prefetchMonthlyFortuneNeighborMonths = () => {
  const today = new Date();
  for (let i = 0; i < 3; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    if (y === paidYear.value && m === paidMonth.value) continue;
    fetchMonthlyFortuneFor(y, m);
  }
};

/** 낭만월운 본문 데이터 fetch 진행 중 명시 플래그 */
const isFetchingMonthlyFortunePaid = ref(false);
const fetchMonthlyFortunePaidNow = async () => {
  isFetchingMonthlyFortunePaid.value = true;
  try {
    await ensureMonthlyFortuneData();
  } finally {
    isFetchingMonthlyFortunePaid.value = false;
  }
};

watch(
  purchasedMonthlyFortuneTypeIds,
  (newVal, oldVal) => {
    if ((newVal?.length || 0) > (oldVal?.length || 0)) {
      invalidateMonthlyFortuneMonth(paidYear.value, paidMonth.value);
      fetchMonthlyFortunePaidNow();
    }
  },
  { deep: true },
);

watch([paidYear, paidMonth], () => {
  fetchMonthlyFortunePaidNow();
});

/** 목차 카드 선택 → 카드별 모바일 풀페이지 콘텐츠로 진입 */
const selectedCard = ref(null);
const handleSelectCard = (card) => {
  selectedCard.value = card;
  // 본문 데이터 fetch 는 refreshMonthlyFortunePageData / 구매 watch 에서 일괄 처리.
  // 여기서 트리거하면 권한 로그가 미반영된 상태로 옛 suggestionFlag 요청이 나갈 수 있음.
  debuggingHandler({ action: 'debug', message: `낭만월운 > 카드 선택: ${card?.key}` });
};
const handleBackFromCardContent = () => {
  selectedCard.value = null;
};

/** 카드 진입 후 데이터 로딩 중 표시 */
const isLoadingMonthlyFortuneData = computed(
  () =>
    !!selectedCard.value &&
    (isFetchingMonthlyFortunePaid.value || !monthlyFortunePaidData.value),
);

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    onClickRecharge(shortageAmount);
  } else {
    const path = '/purchase/points';
    router.push(path);
  }
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  if (window.ReactNativeWebView) {
    onClickRecharge();
  } else {
    router.push('/purchase/tickets');
  }
  showPopAnalysisInfoOther.value = false;
};

/** 낭만월운 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    try {
      await refreshMonthlyFortunePageData();
      await getSajuMonthlyFortuneRate();
      mobileSajuLnbStore.setNeedRefresh(true);
    } catch (e) {
      debuggingHandler({ action: 'error', message: `낭만월운권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);

  if (guestUserId.value) getSajuMonthlyFortuneRate();

  //REVIEW: 테스트 중
  await nextTick();

  if (window.ReactNativeWebView) {
    if (mobileSajuLnbStore.guestUserId) {
      guestUserId.value = mobileSajuLnbStore.guestUserId;
    }
  } else {
    //guestUserId.value = 24825(일반 44페이지)
    //guestUserId.value = 24681(분석 43페이지)
    guestUserId.value = 24844;
    // 웹뷰가 아니면 init 메시지가 안 오므로 초기화 플래그를 직접 해제
    isInitializing.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);

  analysisStore.setMonthlyFortuneIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.monthlyFortune.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.monthlyFortune.value"
    :ticketCountData="serviceData.monthlyFortune.ticketData.value"
    :ticketCount="serviceData.monthlyFortune.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div class="pop-saju-reading-container mobile st-analysis-monthly-fortune">
    <AnalysisMobileHeader title="낭만월운" :guestUserId="guestUserId" @close="close" />
    <div class="pop-ana-saju-reading-inner intro">
      <overviewSajuSummary01
        v-if="!showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.monthlyFortune.logData.value"
        :hasPermission="serviceData.monthlyFortune.permission.value"
        :ticketCountData="serviceData.monthlyFortune.ticketData.value"
        :monthlyFortuneLogData="serviceData.monthlyFortune.logData.value"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="
          showSummary02 &&
          (isInitializing ||
            isLoadingSajuData ||
            isRefreshingPageData ||
            isFetchingMonthlyFortunePaid ||
            !monthlyFortunePaidData)
        "
        :message="'낭만월운 분석중입니다!'"
      />
      <overviewSajuSummary02
        v-if="showSummary02 && !isRefreshingPageData && !selectedCard"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :hasPermission="serviceData.monthlyFortune.permission.value"
        :initializing="isInitializing || isLoadingSajuData || isRefreshingPageData"
        @selectCard="handleSelectCard"
        @updateShowSummary02="
          (value) => {
            showSummary02 = value;
          }
        "
        @goPopAnalysis="showPopAnalysisInfoDo"
        @recharge="onClickRecharge"
        @goPurchaseHistory="handleGoPurchaseHistory"
      />

      <!-- 카드별 모바일 콘텐츠 (상단 오버레이가 로딩 케이스를 모두 커버) -->
      <div
        v-if="
          showSummary02 &&
          selectedCard &&
          monthlyFortunePaidData &&
          !isFetchingMonthlyFortunePaid
        "
        class="mf-card-content"
      >
        <MonthlyFortuneMobileLetter
          v-if="selectedCard.key === 'letter'"
          :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileEnergy
          v-else-if="selectedCard.key === 'energy'"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileLucky
          v-else-if="selectedCard.key === 'lucky'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileRomance
          v-else-if="selectedCard.key === 'romance'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileHealth
          v-else-if="selectedCard.key === 'health'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileRomanceStyle
          v-else-if="selectedCard.key === 'romanceStyle'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileMoney
          v-else-if="selectedCard.key === 'money'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileCapability
          v-else-if="selectedCard.key === 'capability'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileCaution
          v-else-if="selectedCard.key === 'caution'"
          :paidData="monthlyFortunePaidData"
        />
        <MonthlyFortuneMobileMode
          v-else-if="selectedCard.key === 'mode'"
          :selectedMonth="paidMonth"
          :paidData="monthlyFortunePaidData"
        />
        <div v-else class="mf-card-content__placeholder">
          <p>{{ selectedCard.emoji }} {{ selectedCard.title }}</p>
          <p class="hint">선택된 카드: {{ selectedCard.key }} — 콘텐츠 디자인 적용 예정</p>
        </div>
      </div>
    </div>

  </div>

  <!-- TODO: 낭만월운에 맞는 팝업 추가 필요 -->
</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

.pop-ana-saju-reading-inner {
  height: calc(100vh - 52px);
  overflow-y: unset;
  &.intro {
    overflow-y: auto;
  }
  &.st-page {
    height: calc(100vh - 104px);
  }
  .btn-footer-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      // color: var(--Gray-800, #333);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
/* 분석 로딩 오버레이를 viewport 기준 고정 — drawer 등으로 부모 높이가 커져도
   오버레이가 화면 중앙에 표시되도록 한다 */
.pop-saju-reading-container.mobile.st-analysis-monthly-fortune
  .analysis__loading-overlay {
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
}

.st-analysis-monthly-fortune {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    /* PC 낭만월운 목차 배경색과 동일 */
    background: #f9f5eb;
  }
  .pop-ana-saju-reading-inner {
    // padding-bottom: 120px;
  }
}
.st-analysis-monthly-fortune .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

.st-analysis-monthly-fortune .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  // padding-bottom: 60px !important;
  padding-bottom: 0px;
  overflow-x: hidden !important;
}

.mf-card-content {
  min-height: 100%;
  background: #fff;

  &__topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background: #fff;
    border-bottom: 1px solid #ebebeb;

    .back-btn {
      border: none;
      background: transparent;
      color: #5e9f1c;
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
    }
  }

  &__placeholder {
    padding: 24px 16px;
    color: #575757;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 24px;

    p {
      margin: 0 0 8px;
    }
    .hint {
      color: #949494;
      font-size: 13px;
    }
  }
}
</style>
